// Owned by mju-ai-workbench/shared/schemas/teamworkMentions.ts — people in Team Work (0.89.20). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/teamworkMentions.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * People in a Team Work conversation: who a message mentions, and a question an agent asks a named person.
 *
 * A message posted to a Team Work conversation at the project's home (AIOE `conversations.ts`) may carry:
 *  - `mentions`: the people it names with `@` (each a subject of the organisation, with the name as written), and
 *  - `asks`: a question addressed to one person (`to`), which only that person — or whoever covers them under the team's
 *    delegation rules (`teamDelegation.ts`) — may answer with an `answer` message carrying the same `questionId`.
 * The home files the hub records (a `mention` fyi per person, a `question` needs-you for `to`) from the verified message,
 * once, whoever posted it — the workbench, the console or the phone — and refuses a subject who is not a member.
 *
 * Nothing here falls back to anyone on a timer: an agent decides from the delegation rules whether to wait or ask someone
 * else, and says so in the conversation (JP, 26 Oct 2026).
 *
 * Pure: zod and the parsing rules, nothing else.
 */
import { z } from "zod";
/** At most this many people named in one message. */
export const MENTIONS_MAX = 20;
/** A question's offered answers, when it has a short list. */
export const ASK_OPTIONS_MAX = 8;
/** One person a message names. `name` is as written in the message (the person's display name at the time). */
export const teamworkMentionSchema = z.object({
    subjectId: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
});
export const teamworkMentionsSchema = z.array(teamworkMentionSchema).max(MENTIONS_MAX);
/** A question addressed to one person. */
export const teamworkAskSchema = z.object({
    /** Stable for the question's life; an `answer` message names it. */
    questionId: z.string().min(1).max(200),
    /** The person asked. */
    to: z.string().min(1).max(200),
    /** The question in one line (the message's text carries the rest). */
    question: z.string().min(1).max(500),
    /** A short list to pick from, when there is one; absent: a free answer. */
    options: z.array(z.string().min(1).max(120)).min(2).max(ASK_OPTIONS_MAX).optional(),
    /** The delegation topic it falls under, when the asker matched one (`teamDelegation.ts` topic id). */
    topic: z.string().min(1).max(100).optional(),
    /** Whether the asker's work waits on it (true) or carries on meanwhile (false). */
    blocking: z.boolean(),
});
/** What a message adds for people: both optional, both checked by the home against its members. */
export const teamworkMessagePeopleSchema = z.object({
    mentions: teamworkMentionsSchema.optional(),
    asks: teamworkAskSchema.optional(),
});
const isWordChar = (c) => !!c && /[\p{L}\p{N}_]/u.test(c);
/**
 * The people a text names with `@`. A mention is `@` at the start or after a non-word character, followed by a person's
 * display name (case-insensitive); the longest name that matches wins ("@Ana Lopez" over "@Ana"), and it must end at a
 * non-word character or the end. Each person once, in the order first named, at most {@link MENTIONS_MAX}.
 */
export function mentionsIn(text, people) {
    const byLength = [...people].filter((p) => p.name.trim()).sort((a, b) => b.name.length - a.name.length);
    const out = [];
    const seen = new Set();
    const lower = text.toLowerCase();
    for (let i = 0; i < text.length && out.length < MENTIONS_MAX; i++) {
        if (text[i] !== "@" || isWordChar(text[i - 1]))
            continue;
        for (const p of byLength) {
            const name = p.name.trim();
            const end = i + 1 + name.length;
            if (lower.slice(i + 1, end) !== name.toLowerCase() || isWordChar(text[end]))
                continue;
            if (!seen.has(p.subjectId)) {
                seen.add(p.subjectId);
                out.push({ subjectId: p.subjectId, name });
            }
            i = end - 1;
            break;
        }
    }
    return out;
}
//# sourceMappingURL=teamworkMentions.js.map